/**
 * Navigation Items Configuration
 * Role-based navigation menu items for topbar
 * 
 * Based on prototype designs:
 * - FADashboard.tsx: Dashboard, Client Activity, Smart Search Agent, Analytics
 * - ProviderPortal.tsx: Overview, Leads, Profile, Settings
 * - CareSeekerDashboard.tsx: Overview, Messages, Favorites, My Team, Journey, Settings
 */

import {
  Home,
  Users,
  MessageCircle,
  Search,
  FileText,
  Network,
  Sparkles
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type UserRole = 'CARE_SEEKER' | 'CARE_RECIPIENT' | 'SERVICE_PROVIDER' | 'FINANCIAL_ADVISOR' | 'ADMIN';

export type NavigationItem = {
  label: string;
  href: string;
  icon: LucideIcon;
  roles: UserRole[];
  badge?: string; // Optional badge for notifications
};

/**
 * Navigation items configuration
 * Each item specifies which roles can see it
 */
export const navigationItems: NavigationItem[] = [
  // Dashboard (role-specific)
  {
    label: 'Dashboard',
    href: '/dashboard/care-seeker',
    icon: Home,
    roles: ['CARE_SEEKER'],
  },
  {
    label: 'Dashboard',
    href: '/dashboard/service-provider',
    icon: Home,
    roles: ['SERVICE_PROVIDER'],
  },
  {
    label: 'Dashboard',
    href: '/dashboard/financial-advisor',
    icon: Home,
    roles: ['FINANCIAL_ADVISOR'],
  },

  // Messages (all roles)
  {
    label: 'Messages',
    href: '/messages',
    icon: MessageCircle,
    roles: ['CARE_SEEKER', 'SERVICE_PROVIDER', 'FINANCIAL_ADVISOR'],
  },

  // Care Navigator (Care Seekers, Financial Advisors, and Service Providers) - Feature 025, Feature 036
  {
    label: 'Navigator',
    href: '/care-navigator',
    icon: Sparkles,
    roles: ['FINANCIAL_ADVISOR', 'CARE_SEEKER', 'SERVICE_PROVIDER'],
    badge: 'beta',
  },

  // Find Providers (all roles - includes Service Providers per spec)
  {
    label: 'Find Providers',
    href: '/search-providers',
    icon: Search,
    roles: ['CARE_SEEKER', 'SERVICE_PROVIDER', 'FINANCIAL_ADVISOR'],
  },

  // Resources (all roles)
  {
    label: 'Resources',
    href: '/resources',
    icon: FileText,
    roles: ['CARE_SEEKER', 'SERVICE_PROVIDER', 'FINANCIAL_ADVISOR'],
  },

  // My Team (Care Seeker only) - Feature 017: Renamed from Favorites
  {
    label: 'My Team',
    href: '/my-team',
    icon: Users,
    roles: ['CARE_SEEKER'],
  },

  // My Network (Service Provider and Financial Advisor)
  {
    label: 'My Network',
    href: '/network',
    icon: Network,
    roles: ['SERVICE_PROVIDER', 'FINANCIAL_ADVISOR'],
  },

];

/**
 * Get navigation items for a specific role
 * Filters items based on user role
 */
export const getNavigationItemsForRole = (role: UserRole): NavigationItem[] => {
  return navigationItems.filter((item) => item.roles.includes(role));
};

/**
 * Get unique navigation items for a role (prevents duplicates)
 * Some items like "Dashboard" appear for multiple roles with different hrefs
 */
export const getUniqueNavigationItemsForRole = (role: UserRole): NavigationItem[] => {
  const items = getNavigationItemsForRole(role);
  const seen = new Set<string>();
  
  return items.filter(item => {
    const key = `${item.label}-${item.href}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/**
 * Get role display name for UI
 */
export const getRoleDisplayName = (role: UserRole): string => {
  const roleNames: Record<UserRole, string> = {
    CARE_SEEKER: 'Care Seeker Dashboard',
    CARE_RECIPIENT: 'Care Seeker Dashboard',
    SERVICE_PROVIDER: 'Provider Portal',
    FINANCIAL_ADVISOR: 'Financial Advisor Portal',
    ADMIN: 'Admin Portal',
  };
  
  return roleNames[role] || 'Dashboard';
};


